import { useEventCallback, useUpdateEffect } from '@cian/react-utils';
import { Button, Spinner } from '@cian/ui-kit';

import { useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { useSearchOffersDesktopQuery } from '@/packages/query-state/shared/store/search-offers/v3/search-offers-desktop/query';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { memoWithDisplayName } from '@/packages/utils';

import { InfinityLoaderArea } from '../../components/InfinityLoaderArea';

type TInfinityLoadMode = 'infinity' | 'button';

interface IInfinityLoaderAreaContainerProps {
  mode: TInfinityLoadMode;
  onButtonClick: VoidFunction;
  onLoaderReached: VoidFunction;
}

const useBoundActions = boundActionsFactory(reduxActions => ({
  incrementPage: reduxActions.jsonQuery.incrementPage,
}));

export const InfinityLoaderAreaContainer = memoWithDisplayName<IInfinityLoaderAreaContainerProps>(
  ({ mode, onButtonClick, onLoaderReached }) => {
    const { isFetchingNextPage, fetchNextPage, hasNextPage } = useSearchOffersDesktopQuery();

    const boundActions = useBoundActions();

    const infinityAreaDisabled = useRef(false);

    useUpdateEffect(() => {
      if (mode === 'infinity') {
        infinityAreaDisabled.current = false;
      }
    }, [mode]);

    const loadNextPage = useEventCallback(async (callback: VoidFunction) => {
      const { isFetched } = await fetchNextPage();

      /**
       * @description
       * Ждем, пока загрузятся новые офферы.
       * Потом с задержкой меняем состояние лоадера и страницу, чтобы офферы успели отрисоваться
       */
      if (isFetched) {
        setTimeout(() => {
          callback();

          boundActions.incrementPage();
        }, 500);
      }
    });

    const handleLoadMoreClick = useEventCallback(() => {
      loadNextPage(() => {
        onButtonClick();

        infinityAreaDisabled.current = false;
      });
    });

    const { ref } = useInView({
      rootMargin: '0px 0px 352px 0px',
      onChange: inView => {
        if (inView && !infinityAreaDisabled.current) {
          infinityAreaDisabled.current = true;

          loadNextPage(onLoaderReached);
        }
      },
    });

    if (!hasNextPage) {
      return null;
    }

    return (
      <InfinityLoaderArea ref={ref}>
        {mode === 'infinity' && <Spinner size={44} />}

        {mode === 'button' && (
          <Button loading={isFetchingNextPage} size="M" theme="fill_secondary" onClick={handleLoadMoreClick}>
            Показать ещё
          </Button>
        )}
      </InfinityLoaderArea>
    );
  },
  'InfinityLoaderAreaContainer',
);
